import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { Notice } from '@/components/admin/Notice';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/admin/states';
import { deleteEventPhoto, fetchEventPhotos, uploadEventPhoto } from '@/features/events/api';
import { ApiError } from '@/lib/api/http';

/**
 * イベントの「当日写真」タブ（D-186）.
 *
 * 正仕様書の月例会フロー「理事講師：翌朝に事後アンケートと当日写真を送付」に対応。
 * アップロードは担当の理事・講師と事務局、閲覧は対象の受講生も可（認可はAPI側 / §0-6）。
 */
export function EventPhotosTab({ eventId }: { eventId: string }): ReactElement {
  const { user } = useAuth();
  // 理事・サクセスは担当した回しか一覧に出ない（D-182）ため、ここに来られた時点で
  // ほぼ担当者。念のためAPI側でも担当か確認している
  const canUpload =
    user?.active_role === 'office' ||
    user?.active_role === 'director' ||
    user?.active_role === 'success';
  const isOffice = user?.active_role === 'office';
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['event-photos', eventId],
    queryFn: () => fetchEventPhotos(eventId),
  });
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onUpload(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      for (const file of Array.from(files)) {
        await uploadEventPhoto(eventId, file);
      }
      await queryClient.invalidateQueries({ queryKey: ['event-photos', eventId] });
      setNotice(`${files.length}枚の写真を登録しました。`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'アップロードに失敗しました。');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function onDelete(photoId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteEventPhoto(eventId, photoId);
      await queryClient.invalidateQueries({ queryKey: ['event-photos', eventId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '削除に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}

      {canUpload && (
        <div>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover">
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              disabled={busy}
              onChange={(e) => void onUpload(e.target.files)}
              className="hidden"
            />
            {busy ? 'アップロード中…' : '写真をアップロード'}
          </label>
          <p className="mt-1 text-xs text-wptext-muted">
            JPEG・PNG・WebP。複数枚まとめて選べます。登録した写真はこの回の対象の受講生にも表示されます。
          </p>
        </div>
      )}

      {query.isPending ? (
        <SkeletonRows rows={2} columns={3} />
      ) : query.isError ? (
        <ErrorState
          message="当日写真を読み込めませんでした。"
          onRetry={() => void query.refetch()}
        />
      ) : query.data.length === 0 ? (
        <EmptyState
          title="当日写真はまだありません"
          description={
            canUpload ? '開催後、当日の写真をアップロードしてください。' : undefined
          }
        />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {query.data.map((photo) => (
            <li key={photo.id} className="rounded-sm border border-wpborder bg-white p-2">
              <a href={photo.url} target="_blank" rel="noreferrer">
                <img
                  src={photo.url}
                  alt={photo.original_name ?? '当日写真'}
                  className="h-36 w-full rounded-sm object-cover"
                />
              </a>
              <p className="mt-1 truncate text-xs text-wptext-muted">
                {photo.uploaded_by_name ?? '—'} ・{' '}
                {new Date(photo.created_at).toLocaleDateString('ja-JP')}
              </p>
              {(isOffice || photo.uploaded_by_name === (user?.display_name ?? user?.email)) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onDelete(photo.id)}
                  className="mt-1 text-xs text-wpnotice-error hover:underline disabled:opacity-50"
                >
                  削除
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
